import { assertUnreachable } from '@sgme/fp';
import classNames from 'classnames';
import { SpDropdownToggle } from 'components/shared/Dropdown';
import type { IntlKey } from 'locales';
import { FC, ReactNode, useCallback, useState } from 'react';
import { Dropdown, DropdownItem, DropdownMenu } from 'react-bootstrap';
import { useIntl } from 'react-intl';
import { SidePanelTabId } from 'state/uiPanelState/sidePanelUi/sidePanelUi.model';

export const headerItemMaxWidth = 120;

interface TopTabHeaderItemProps {
  children?: ReactNode;
  id: SidePanelTabId;
  activeTabId: SidePanelTabId;
  translationKey?: IntlKey;
  tooltipTranslationKey?: IntlKey;
  handleChangeOfActiveTab: Nullable<(id: SidePanelTabId) => void>;
}

function getHeaderTitleTranslationKey(id: SidePanelTabId) {
  switch (id) {
    case 'quotation':
      return 'sidePanel.tabs.quotation';
    case 'regulatory':
      return 'sidePanel.tabs.regulatory';
    case 'advisory':
      return 'sidePanel.tabs.advisory';
    case 'missedDealFeedback':
      return 'sidePanel.tabs.missedDealFeedback';
    case 'greeksFeesMetrics':
      return 'sidePanel.tabs.greeksFeesMetrics';
    case 'backTest':
      return 'sidePanel.tabs.backTest';
    default:
      return assertUnreachable(id, 'Unhandled side panel tab ID');
  }
}

export const HeaderItem: FC<TopTabHeaderItemProps> = ({
  handleChangeOfActiveTab,
  id,
  activeTabId,
  translationKey,
  tooltipTranslationKey,
  children,
}) => {
  const { formatMessage } = useIntl();
  const isActivated = id === activeTabId;

  const onActivate = useCallback(() => {
    if (handleChangeOfActiveTab !== null) {
      handleChangeOfActiveTab(id);
    }
  }, [handleChangeOfActiveTab, id]);

  return (
    <li data-e2e={id} className="nav-item" onClick={onActivate}>
      <a className={classNames('nav-link', { active: isActivated })}>
        <h5
          className="text-truncate fs-4"
          title={formatMessage({
            id: tooltipTranslationKey ?? translationKey ?? getHeaderTitleTranslationKey(id),
          })}
        >
          {formatMessage({ id: translationKey ?? getHeaderTitleTranslationKey(id) })}
          {children}
        </h5>
      </a>
    </li>
  );
};

export const HeaderDropdown: FC<{
  children: ReactNode;
}> = ({ children }) => {
  const [isDropdownOpened, setIsDropdownOpened] = useState(false);

  const toggleDropdown = useCallback(() => setIsDropdownOpened(!isDropdownOpened), [isDropdownOpened]);

  return (
    <Dropdown show={isDropdownOpened} onToggle={toggleDropdown} as="li" data-e2e="sidepanel-tabs-dropdown">
      <SpDropdownToggle caret={false} variant="link" className="btn px-0 block btn-icon btn-flat-secondary" size="lg">
        <i className="icon icon-md">more_vert</i>
      </SpDropdownToggle>
      <DropdownMenu as="ul">{children}</DropdownMenu>
    </Dropdown>
  );
};

export const HeaderDropdownItem: FC<TopTabHeaderItemProps> = ({
  handleChangeOfActiveTab,
  id,
  activeTabId,
  translationKey,
  tooltipTranslationKey,
}) => {
  const { formatMessage } = useIntl();
  const isActivated = id === activeTabId;

  const onActivate = useCallback(() => {
    if (handleChangeOfActiveTab !== null) {
      handleChangeOfActiveTab(id);
    }
  }, [handleChangeOfActiveTab, id]);

  return (
    <DropdownItem as="a" data-e2e={id} className={classNames({ active: isActivated })} onClick={onActivate}>
      <span
        className="text-truncate"
        title={formatMessage({
          id: tooltipTranslationKey ?? translationKey ?? getHeaderTitleTranslationKey(id),
        })}
      >
        {formatMessage({ id: translationKey ?? getHeaderTitleTranslationKey(id) })}
      </span>
    </DropdownItem>
  );
};
